import { SeoController, useSocialShareModal } from '@application/components';
import { RedirectWithStatus } from '@application/components/app/App/RedirectWithStatus';
import type { PageState } from '@application/types';
import {
  emailShareUrl,
  facebookShareUrl,
  languageCodeToLanguage,
  twitterShareUrl,
  useNewsletter,
  useRedirectArticles,
  useRouter,
  useTranslatedUrl,
  waShareUrl,
} from '@application/utils';
import { ArticlePage } from '@presentation/pages';
import { PageSkeleton } from '@presentation/pages/Article/components/PageSkeleton';
import type { LocationDescriptor } from 'history';
import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useFormatArticles } from '../ArticleListPageController/api/useFormatArticles';
import { useFormatArticle } from './api/useFormatArticle';
import { useGetArticle } from './api/useGetArticle';

export const ArticlePageController: FC = () => {
  const {
    translatedRoute,
    infoFromCurrentPath,
    languageCode,
    routeLocationDescriptor,
    getLocaleLinksForTranslations,
  } = useRouter();
  const { pathname } = useLocation();
  const { show: showShareModal, setUrls } = useSocialShareModal();

  const id = infoFromCurrentPath.params[0];

  const { article, articles, articleLoading, articleError } = useGetArticle(id);
  const { formattedArticle } = useFormatArticle(article);
  const { formattedArticles } = useFormatArticles(articles);

  const { newsletterProps } = useNewsletter();

  const currentTranslation = useMemo(() => {
    if (!article) return;
    return article.translations.find((t) => t.languageCode === languageCode);
  }, [article, languageCode]);

  const translations = useMemo(() => {
    if (!article) return;
    return article?.translations.map((t) => ({
      ...t,
      language: languageCodeToLanguage(t.languageCode),
    }));
  }, [article]);

  useTranslatedUrl(translations);
  useRedirectArticles();

  const pageState = useMemo((): PageState => {
    if (articleLoading) return 'loading';
    if (articleError) return 'error';
    if (!formattedArticle?.contentHtml) return 'empty';
    return 'data';
  }, [articleError, articleLoading, formattedArticle?.contentHtml]);

  const backLink = useMemo((): LocationDescriptor => {
    return routeLocationDescriptor('articleList', {
      search: infoFromCurrentPath?.state?.returnSearch ?? {},
    });
  }, [infoFromCurrentPath?.state?.returnSearch, routeLocationDescriptor]);

  const articlesLink = routeLocationDescriptor('articleList');

  const socialShareClickHandler = useCallback(() => {
    const url = `https://tipaw.com${pathname}`;

    showShareModal();

    setUrls({
      facebook: facebookShareUrl(url),
      twitter: twitterShareUrl(url, currentTranslation?.title ?? ''),
      whatsapp: waShareUrl(url),
      email: emailShareUrl(
        url,
        currentTranslation?.title ?? '',
        `${currentTranslation?.title}\n\n${currentTranslation?.seoDescription}`,
      ),
    });
  }, [
    currentTranslation?.seoDescription,
    currentTranslation?.title,
    pathname,
    setUrls,
    showShareModal,
  ]);

  return (
    <>
      <SeoController
        title={currentTranslation?.seoTitle ?? undefined}
        description={currentTranslation?.seoDescription ?? undefined}
        keywords={currentTranslation?.seoKeywords ?? undefined}
        thumbnail={article?.coverImg ?? undefined}
        localeLinks={getLocaleLinksForTranslations(translations)}
      />
      {pageState === 'loading' && <PageSkeleton backLink={backLink} />}
      {(pageState === 'empty' || pageState === 'error') && (
        <RedirectWithStatus status={302} to={translatedRoute('articleList')} />
      )}
      {pageState === 'data' && (
        <ArticlePage
          {...{
            ...formattedArticle,
            header: {
              ...formattedArticle.header,
              backLink,
            },
          }}
          newsletter={newsletterProps}
          moreArticleLink={articlesLink}
          articleItems={formattedArticles}
          loading={articleLoading}
          onSocialShareClick={socialShareClickHandler}
        />
      )}
    </>
  );
};
